/**
 * Stripe Checkout intégré (embedded) : reste dans l’app, iframe Stripe, puis retour sur la même URL (return_url).
 * - Prod (Vercel) : même origine → `/api/stripe/create-checkout-session`.
 * - Dev : appel direct `http://127.0.0.1:4242` (évite les soucis de proxy Vite sur POST).
 * - Sinon : `VITE_PUBLIC_CHECKOUT_API_ORIGIN`.
 *
 * Clé publique : `VITE_STRIPE_PUBLISHABLE_KEY` (pk_test_… / pk_live_…).
 */
import { loadStripe } from '@stripe/stripe-js';
import { markSuggestionSent, pushHistoryEntry } from './profile.js';

function checkoutApiOrigin() {
  const explicit = (import.meta.env.VITE_PUBLIC_CHECKOUT_API_ORIGIN || '').replace(/\/$/, '');
  if (explicit) return explicit;
  if (import.meta.env.DEV) return 'http://127.0.0.1:4242';
  return '';
}

function createCheckoutUrl() {
  return `${checkoutApiOrigin()}/api/stripe/create-checkout-session`;
}

function formatCheckoutHttpError(res, data) {
  if (typeof data.error === 'string') {
    const extra = [data.hint, data.details].filter(Boolean).join(' — ');
    return extra ? `${data.error} — ${extra}` : data.error;
  }
  const combined = [data.error, data.hint].filter(Boolean).join(' — ');
  if (combined) return combined;
  if (res.status === 404) {
    return data.error === 'route_inconnue'
      ? `Route inconnue (${data.path ?? ''}) : préfixe attendu /api/stripe/create-checkout-session.`
      : 'Réponse 404 : redémarre `npm run stripe-server` ou vérifie l’URL du checkout.';
  }
  if (res.status === 502) {
    return typeof data.message === 'string'
      ? data.message
      : 'Erreur 502 : vérifie la réponse JSON du serveur et les logs `stripe-server`.';
  }
  return 'Paiement indisponible pour le moment.';
}

/**
 * @param {string} bidId
 * @param {string} returnUrl
 * @returns {Promise<{ ok: true, clientSecret: string } | { ok: false, message: string }>}
 */
export async function fetchEmbeddedCheckoutForBid(bidId, returnUrl) {
  try {
    const res = await fetch(createCheckoutUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bidId: String(bidId), returnUrl }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        message: formatCheckoutHttpError(res, data),
      };
    }
    if (!data.clientSecret || typeof data.clientSecret !== 'string') {
      return { ok: false, message: 'Réponse Stripe inattendue (client_secret manquant).' };
    }
    return { ok: true, clientSecret: data.clientSecret };
  } catch (e) {
    return {
      ok: false,
      message:
        e?.message?.includes('Failed to fetch') || e?.name === 'TypeError'
          ? 'Paiement injoignable : lance `npm run dev:with-stripe` (ou `npm run stripe-server` avec `npm run dev`).'
          : (e?.message ?? 'Erreur réseau'),
    };
  }
}

/**
 * Suggestion avec montant : paiement d’abord, la ligne `requests` est créée par le webhook Stripe.
 * @param {object} payload
 * @param {string} returnUrl
 * @returns {Promise<{ ok: true, clientSecret: string } | { ok: false, message: string }>}
 */
export async function fetchEmbeddedCheckoutForPaidSuggestion(payload, returnUrl) {
  try {
    const res = await fetch(createCheckoutUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        returnUrl,
        paidSuggestion: {
          eventId: payload.eventId,
          clientUid: payload.clientUid,
          barUserId: payload.barUserId ?? null,
          amountCents: payload.amountCents,
          title: payload.title,
          artist: payload.artist,
          deezerTrackId: payload.deezerTrackId,
          deezerPreviewUrl: payload.deezerPreviewUrl ?? null,
          deezerCoverUrl: payload.deezerCoverUrl ?? null,
        },
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        message: formatCheckoutHttpError(res, data),
      };
    }
    if (!data.clientSecret || typeof data.clientSecret !== 'string') {
      return { ok: false, message: 'Réponse Stripe inattendue (client_secret manquant).' };
    }
    return { ok: true, clientSecret: data.clientSecret };
  } catch (e) {
    return {
      ok: false,
      message:
        e?.message?.includes('Failed to fetch') || e?.name === 'TypeError'
          ? 'Paiement injoignable : lance `npm run dev:with-stripe` (ou `npm run stripe-server` avec `npm run dev`).'
          : (e?.message ?? 'Erreur réseau'),
    };
  }
}

/**
 * @param {string} clientSecret
 * @param {HTMLElement} mountEl
 * @returns {Promise<{ ok: true, destroy: () => void } | { ok: false, message: string }>}
 */
export async function mountEmbeddedStripeCheckout(clientSecret, mountEl) {
  const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!pk || typeof pk !== 'string') {
    return {
      ok: false,
      message:
        'Clé publique Stripe manquante : ajoute `VITE_STRIPE_PUBLISHABLE_KEY` (pk_…) dans `.env.local` / Vercel.',
    };
  }
  try {
    const stripe = await loadStripe(pk);
    if (!stripe) {
      return { ok: false, message: 'Impossible de charger Stripe.js.' };
    }
    const checkout = await stripe.createEmbeddedCheckoutPage({
      fetchClientSecret: async () => clientSecret,
    });
    checkout.mount(mountEl);
    return {
      ok: true,
      destroy: () => {
        try {
          checkout.destroy();
        } catch {
          /* ignore */
        }
      },
    };
  } catch (e) {
    return {
      ok: false,
      message: e instanceof Error ? e.message : 'Erreur au montage du paiement Stripe.',
    };
  }
}

/**
 * Profil + historique avant paiement (même logique qu’avant la redirection).
 * @param {object} payload
 */
export function preparePaidSuggestionClientState(payload) {
  markSuggestionSent();
  pushHistoryEntry({
    id: `paid-${Date.now()}`,
    title: payload.title,
    artist: payload.artist,
    bid_amount: payload.amountCents,
  });
}
